'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';

import styles from './CodeInput.module.scss';

export type CodeInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
};

const DEFAULT_LENGTH = 6;

const sanitizeValue = (value: string, length: number) =>
  value.replace(/\D/g, '').slice(0, length);

export function CodeInput({
  length = DEFAULT_LENGTH,
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
}: CodeInputProps) {
  const inputLength = Math.max(1, Math.floor(length));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const { sanitizedValue, digits } = useMemo(() => {
    const sanitized = sanitizeValue(value ?? '', inputLength);
    const digitArray = Array.from(
      { length: inputLength },
      (_, index) => sanitized[index] ?? '',
    );

    return { sanitizedValue: sanitized, digits: digitArray };
  }, [value, inputLength]);

  useEffect(() => {
    if (sanitizedValue !== value) {
      onChange(sanitizedValue);
    }
  }, [sanitizedValue, value, onChange]);

  const emitDigits = useCallback(
    (nextDigits: string[]) => {
      const nextValue = nextDigits.join('');
      onChange(nextValue);

      const isComplete =
        nextDigits.length === inputLength &&
        nextDigits.every((digit) => digit !== '');

      if (isComplete) {
        onComplete?.(nextValue);
      }
    },
    [inputLength, onChange, onComplete],
  );

  const focusInput = useCallback(
    (index: number) => {
      if (disabled) return;
      const element = inputRefs.current[index];

      if (!element) return;
      requestAnimationFrame(() => {
        element.focus();
        element.select();
      });
    },
    [disabled],
  );

  useEffect(() => {
    if (autoFocus && !disabled) {
      focusInput(0);
    }
  }, [autoFocus, disabled, focusInput]);

  const handleInputChange = useCallback(
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      const rawValue = event.target.value;
      const digit = rawValue.replace(/\D/g, '').slice(-1);
      const nextDigits = [...digits];

      nextDigits[index] = digit;
      emitDigits(nextDigits);

      if (digit && index < inputLength - 1) {
        focusInput(index + 1);
      }
    },
    [disabled, digits, emitDigits, focusInput, inputLength],
  );

  const handleKeyDown = useCallback(
    (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (event.key === 'Backspace') {
        event.preventDefault();
        if (digits[index]) {
          const nextDigits = [...digits];
          nextDigits[index] = '';
          emitDigits(nextDigits);
          return;
        }

        if (index > 0) {
          focusInput(index - 1);
          const nextDigits = [...digits];
          nextDigits[index - 1] = '';
          emitDigits(nextDigits);
        }
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault();
        focusInput(index - 1);
      } else if (event.key === 'ArrowRight' && index < inputLength - 1) {
        event.preventDefault();
        focusInput(index + 1);
      }
    },
    [disabled, digits, emitDigits, focusInput, inputLength],
  );

  const handlePaste = useCallback(
    (index: number) => (event: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return;
      event.preventDefault();
      const pasteData = event.clipboardData.getData('text') ?? '';
      const sanitized = pasteData.replace(/\D/g, '');

      if (!sanitized) {
        return;
      }

      const nextDigits = [...digits];
      let cursor = index;

      for (const char of sanitized) {
        if (cursor >= inputLength) break;
        nextDigits[cursor] = char;
        cursor += 1;
      }

      emitDigits(nextDigits);

      const nextFocusIndex = cursor < inputLength ? cursor : inputLength - 1;
      focusInput(nextFocusIndex);
    },
    [disabled, digits, emitDigits, focusInput, inputLength],
  );

  const handleFocus = useCallback(
    (index: number) => () => {
      if (disabled) return;
      const target = inputRefs.current[index];
      if (!target) return;
      requestAnimationFrame(() => {
        target.select();
      });
    },
    [disabled],
  );

  return (
    <div className={styles.wrapper} data-disabled={disabled || undefined}>
      {digits.map((digit, index) => {
        const inputClassNames = [
          styles.input,
          digit ? styles.inputFilled : '',
          disabled ? styles.inputDisabled : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <input
            key={`${index}-${inputLength}`}
            ref={(element) => {
              inputRefs.current[index] = element;
            }}
            className={inputClassNames}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d*"
            maxLength={1}
            value={digit}
            onChange={handleInputChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste(index)}
            onFocus={handleFocus(index)}
            disabled={disabled}
            aria-label={`Digit ${index + 1}`}
          />
        );
      })}
    </div>
  );
}
